export const e2en = {
  meta: {
    title: 'Water Leak Detection for Insurance | E2 Insurance',
    description: 'E2 Insurance helps insurers detect water leaks earlier, enable faster intervention and reduce the severity of water-damage claims through connected risk prevention.',
    twitterDescription: 'Connected risk prevention for modern insurers. Detect earlier, react faster, reduce water-damage claim severity.',
  },
  hero: {
    badge: 'E2 Insurance — Connected Risk Prevention',
    h1: 'Prevent water damage before it becomes a major claim.',
    sub: 'E2 Insurance helps insurers detect water leaks early, alert the right people instantly and reduce the impact of water-damage incidents.',
    supporting: 'From claims management to active risk prevention.',
    ctaPrimary: 'Discover E2 Insurance',
    ctaSecondary: 'Talk to us',
    trustLine: 'Designed for insurers, assistance operators and modern home insurance products.',
    liveLabel: 'Live incident',
    resolved: 'RESOLVED',
    incidentTitle: 'Water detected — Kitchen',
    incidentLoc: 'Riga · Apartment 4B',
    steps: [
      ['02:14', 'Water detected under kitchen sink'],
      ['02:15', 'Customer notified'],
      ['02:17', 'Customer acknowledged alert'],
      ['02:46', 'Incident resolved'],
    ],
    tta: 'Time to acknowledge',
    ttaVal: '3 min',
    ttr: 'Time to resolution',
    ttrVal: '32 min',
  },
  problem: {
    label: 'The problem',
    h2: 'Water damage becomes expensive when nobody knows it is happening.',
    p1: 'A small leak can remain unnoticed for hours or days. By the time the policyholder discovers it, the damage may already involve floors, walls, furniture, neighbouring properties and significant remediation costs.',
    p2: 'Traditional insurance starts when the claim is reported.',
    highlight: 'E2 Insurance starts when the incident begins.',
    statement: 'The most expensive leak is the one nobody knows about.',
  },
  compare: {
    label: 'The difference',
    h2: 'Time changes the outcome.',
    withoutTitle: 'Without early detection',
    withoutSteps: ['Leak starts', 'Leak remains unnoticed', 'Water damage spreads', 'Customer discovers damage', 'Claim is reported', 'Remediation begins'],
    withoutNote: 'Hours or days can pass before anyone reacts.',
    withTitle: 'With E2 Insurance',
    withSteps: ['Leak starts', 'Water detected', 'Customer alerted', 'Incident acknowledged', 'Action taken', 'Damage potentially limited'],
    withNote: 'Minutes between detection and action.',
    conclusion: 'E2 turns time into a risk-reduction tool.',
  },
  value: {
    label: 'How it works',
    h2: 'Detect earlier. React faster. Reduce damage.',
    stepLabel: 'Step',
    steps: [
      { title: 'Detect', desc: 'Water is identified as soon as an incident occurs.' },
      { title: 'Alert', desc: 'The right people are notified automatically.' },
      { title: 'React', desc: 'The policyholder, assistance provider or maintenance team can intervene quickly.' },
      { title: 'Document', desc: 'Every incident and action can be recorded for operational follow-up and analysis.' },
    ],
  },
  metrics: {
    label: 'Measurable prevention',
    h2: 'Measure prevention through response time.',
    items: [
      { label: 'Time to Detect', desc: 'How quickly the incident is identified.' },
      { label: 'Time to Acknowledge', desc: 'How quickly someone confirms the alert.' },
      { label: 'Time to Resolve', desc: 'How quickly the situation is brought under control.' },
    ],
    statement: 'Prevention becomes measurable when every incident has a timeline.',
  },
  gains: {
    label: 'Insurance value',
    h2: 'Turn prevention into measurable insurance value.',
    items: [
      { title: 'Reduce claim severity', desc: 'Earlier detection can limit the extent of damage before intervention.' },
      { title: 'Reduce response time', desc: 'Alerts are generated automatically instead of relying on someone eventually discovering the problem.' },
      { title: 'Improve customer experience', desc: 'The insurer helps customers prevent damage instead of only compensating them afterwards.' },
      { title: 'Create actionable risk data', desc: 'Generate structured information about incidents, response times and prevention performance.' },
      { title: 'Strengthen customer retention', desc: 'Risk-prevention services can become a tangible benefit attached to home insurance products.' },
      { title: 'Differentiate insurance products', desc: 'Offer customers a proactive service beyond traditional insurance coverage and pricing.' },
    ],
  },
  incident: {
    label: 'Incident traceability',
    h2: 'From incident to resolution, every second is visible.',
    sub: 'Every incident creates a complete, time-stamped audit trail — from first detection to final resolution.',
    cardTitle: 'Water detected — Kitchen',
    cardId: 'Incident #2481 · Sample event',
    status: 'Resolved',
    events: [
      ['02:14', 'Water detected under kitchen sink'],
      ['02:15', 'Customer notified'],
      ['02:17', 'Customer acknowledged alert'],
      ['02:21', 'Assistance contacted'],
      ['02:29', 'Action started'],
      ['02:46', 'Incident resolved'],
    ],
    tta: 'Time to acknowledge',
    ttaVal: '3 min',
    ttr: 'Time to resolution',
    ttrVal: '32 min',
    note: 'Conceptual example for illustration — not real operational statistics.',
  },
  customer: {
    label: 'Customer experience',
    h2: 'Protection without complexity.',
    p1: 'E2 Insurance is designed to operate automatically in the background. The policyholder does not need to understand or monitor technical infrastructure.',
    p2: 'When something happens, the customer receives a simple, actionable alert.',
    e2homeSub: 'Simple protection for the policyholder.',
    features: [
      'Property protection status',
      'Active alerts',
      'Location of detected incidents',
      'Push notifications',
      'Incident acknowledgement',
      'Simple instructions',
      'Incident history',
      'Contact assistance',
      'Confirmation when the incident is resolved',
    ],
    highlight: 'The app exists for one reason: help the customer act quickly when something happens.',
    mockup: {
      homeStatus: 'Home status',
      protected: 'Protected',
      alertTitle: 'Water detected',
      alertLoc: 'Kitchen · Under sink · 14:32',
      recommended: 'Recommended action',
      recommendedAction: 'Check the area immediately.',
      btnCheck: 'I am checking',
      btnAssist: 'Contact assistance',
    },
  },
  control: {
    label: 'For the insurer',
    h2: 'One view across the insured portfolio.',
    sub: 'The E2 Insurance Control Center gives operational teams real-time visibility into every protected property and every active incident.',
    sampleTag: 'SAMPLE INTERFACE — ILLUSTRATIVE VALUES',
    kpis: [
      { label: 'Protected properties', value: '12,482' },
      { label: 'Active incidents', value: '7' },
      { label: 'Average acknowledgement time', value: '2m 18s' },
      { label: 'Resolved incidents this month', value: '164' },
    ],
    tableHeaders: ['Property', 'Incident type', 'Location', 'Detected', 'Acknowledged', 'Assistance', 'Status'],
    incidents: [
      { property: 'Riga – Apartment 4B', type: 'Water detected', location: 'Kitchen', detected: '08:42', ack: '08:45', assistance: 'Not required', status: 'Resolved' },
      { property: 'Vilnius – House 12', type: 'Water detected', location: 'Bathroom', detected: '09:15', ack: '09:17', assistance: 'Contacted', status: 'In progress' },
      { property: 'Tallinn – Apartment 7A', type: 'Water detected', location: 'Utility room', detected: '10:03', ack: '—', assistance: '—', status: 'New' },
      { property: 'Kaunas – Townhouse 3', type: 'Water detected', location: 'Under sink', detected: '10:28', ack: '10:31', assistance: 'Dispatched', status: 'Assistance contacted' },
      { property: 'Riga – Apartment 22C', type: 'Water detected', location: 'Boiler room', detected: '11:05', ack: '11:06', assistance: 'Not required', status: 'Acknowledged' },
    ],
    detectedShort: 'Detected',
    note: 'All figures shown are conceptual UI examples, not real operational data.',
  },
  portfolio: {
    label: 'Portfolio intelligence',
    h2: 'Turn incidents into portfolio intelligence.',
    sub: 'E2 Insurance can help insurers understand prevention performance across their portfolio — not just resolve individual incidents.',
    indicators: [
      'Number of protected properties',
      'Number of detected incidents',
      'Incident frequency',
      'Average acknowledgement time',
      'Average resolution time',
      'Recurring incidents',
      'Locations with repeated events',
      'Unresolved alerts',
      'Protection availability',
      'Prevention performance over time',
    ],
    highlight: 'Individual alerts solve incidents. Portfolio intelligence helps insurers improve prevention strategy.',
  },
  assist: {
    label: 'Optional — for intervention partners',
    h2: 'Connect assistance directly to the incident.',
    p1: 'can provide assistance operators or service providers with a focused view of incidents requiring intervention.',
    note: 'Insurers choose their own operational model — E2 does not require insurers to use E2 Assist.',
    panelTitle: 'E2 Assist — possible functions',
    functions: [
      'Assigned incidents',
      'Property information',
      'Incident location',
      'Detection time',
      'Customer status',
      'Actions required',
      'Intervention acknowledgement',
      'Incident closure',
      'Notes',
    ],
  },
  platform: {
    label: 'Platform',
    h2: 'One prevention platform. Three operational experiences.',
    items: [
      { name: 'E2 Home', who: 'For the policyholder' },
      { name: 'E2 Insurance Control Center', who: 'For the insurer' },
      { name: 'E2 Assist', who: 'For assistance and intervention partners' },
    ],
    statement: 'E2 connects detection, customer response, insurance operations and assistance into one measurable incident workflow.',
  },
  workflow: {
    label: 'Workflow',
    h2: 'From detection to resolution.',
    steps: [
      'Water detected',
      'Customer notified',
      'Customer acknowledges',
      'Assistance notified if required',
      'Action taken',
      'Incident resolved',
      'Event recorded',
    ],
    note: "E2 Insurance can adapt notification and escalation rules to each insurer's operating model.",
  },
  fits: {
    label: 'Flexible operations',
    h2: 'Your insurance workflow. Your rules.',
    sub: 'E2 does not force insurers into a specific operating model. An incident can trigger different workflows.',
    models: [
      { title: 'Customer-first', desc: 'Alert the policyholder immediately and escalate only if there is no response.' },
      { title: 'Assistance-first', desc: 'Notify an assistance centre at the same time as the customer.' },
      { title: 'Service-provider escalation', desc: 'Send selected incidents to an approved intervention partner.' },
      { title: 'Insurer-controlled', desc: "Route incidents through the insurer's own operational environment." },
    ],
    highlight: 'E2 adapts to the insurer — not the other way around.',
  },
  opportunity: {
    label: 'Product opportunity',
    h2: 'Make prevention part of the insurance product.',
    modelLabel: 'Model',
    models: [
      { title: 'Included protection', desc: 'Offer E2 Insurance as part of selected home insurance policies.' },
      { title: 'Premium protection', desc: 'Offer it as an additional prevention service.' },
      { title: 'High-risk properties', desc: 'Deploy protection where the potential severity of water damage is particularly high.' },
      { title: 'Portfolio prevention', desc: 'Use E2 Insurance across selected customer segments to reduce overall exposure.' },
      { title: 'White-label service', desc: "Deliver the service under the insurer's own brand." },
    ],
  },
  calculator: {
    label: 'Scenario modelling',
    h2: 'Explore the potential economics of prevention.',
    sub: 'Enter your own portfolio assumptions to model an illustrative scenario. No E2 assumptions are pre-loaded as facts.',
    inputsTitle: 'Your assumptions',
    properties: 'Number of insured properties',
    frequency: 'Annual water-damage claim frequency (%)',
    avgCost: 'Average cost per water-damage claim (€)',
    reduction: 'Assumed reduction in average claim severity (%)',
    reductionNote: 'Your own assumption — not an E2 performance claim.',
    resultsTag: 'Illustrative scenario based on your inputs',
    claims: 'Estimated annual water-damage claims',
    exposure: 'Estimated annual water-damage exposure',
    avoided: 'Potential avoided loss based on your assumptions',
    disclaimer: 'This calculator is provided for scenario modelling only. Results are based entirely on user-entered assumptions and do not represent guaranteed savings or E2 performance claims.',
  },
  businessCase: {
    label: 'The business case',
    h2: 'The equation is simple.',
    statement: 'How much damage can be avoided by detecting an incident earlier?',
    support: 'For an insurer, even a relatively small reduction in average claim severity can create significant value when applied across a large insured portfolio.',
    highlight: 'E2 Insurance is designed to make prevention measurable.',
  },
  trust: {
    label: 'Trust & governance',
    h2: 'Built for trusted insurance operations.',
    items: [
      { title: 'Secure access', desc: 'Role-based access to operational information.' },
      { title: 'Incident traceability', desc: 'Events and operational actions can be recorded throughout the incident lifecycle.' },
      { title: 'Data protection', desc: 'Designed with data protection and controlled access in mind.' },
      { title: 'Configurable data retention', desc: 'Retention policies can be configured according to operational and regulatory requirements.' },
      { title: 'Operational visibility', desc: 'Insurers can monitor service and protection availability.' },
    ],
  },
  adapt: {
    label: 'Adaptability',
    h2: 'Technology should adapt to insurance — not the other way around.',
    sub: 'E2 Insurance can operate as an independent prevention service or integrate with existing insurance and assistance environments.',
    caps: [
      'Customer notification rules',
      'Assistance processes',
      'Escalation procedures',
      'Claims workflows',
      'Insurer branding',
      'Portfolio segmentation',
      'Operational reporting',
      'Integration with existing systems',
    ],
  },
  whiteLabel: {
    label: 'Deployment model',
    h2: 'Your brand. Your customer relationship.',
    p: 'E2 Insurance can support a white-label deployment model: the insurer can offer the prevention service under its own brand while E2 provides the underlying prevention infrastructure.',
    panelTitle: 'Configurable elements',
    elements: ['Branding', 'Customer communication', 'Notification workflows', 'Assistance processes', 'Operational interfaces'],
  },
  faq: {
    label: 'FAQ',
    h2: 'Water leak detection for insurance — common questions.',
    items: [
      { q: 'What is water leak detection for insurance?', a: 'Water leak detection for insurance uses connected detection to identify water incidents as early as possible. Instead of waiting for a policyholder to discover damage, the system alerts customers, insurers or assistance providers as soon as water is detected, so someone can respond before the damage becomes more severe.' },
      { q: 'How can early water leak detection help insurers?', a: 'Earlier detection creates time for intervention. That time can translate into faster response, potentially reduced claim severity, a better customer experience and structured incident data. Insurers gain visibility into how incidents unfold and how quickly they are resolved across the insured portfolio.' },
      { q: 'Who receives a water leak alert?', a: 'Notification and escalation rules are configurable according to the insurer\u2019s operating model. Alerts can go to the policyholder first, to an assistance centre in parallel, to an approved intervention partner, or through the insurer\u2019s own operational environment.' },
      { q: 'Does the customer need to monitor the system?', a: 'No. The service is designed to operate automatically in the background. The policyholder does not need to check dashboards or monitor equipment. When something happens, the customer receives a simple, actionable alert with clear recommended steps.' },
      { q: 'Can E2 Insurance integrate with an existing insurance application?', a: 'E2 Insurance is designed to support integration with existing insurance and assistance environments. Insurers can embed prevention alerts and incident data into their own customer and operational workflows.' },
      { q: 'Can E2 Insurance be white-labelled?', a: 'Yes. The platform can support insurer-branded deployment models, where the insurer offers the prevention service under its own brand while E2 provides the underlying prevention infrastructure.' },
      { q: 'Can insurers monitor multiple properties?', a: 'Yes. The E2 Insurance Control Center is designed to provide portfolio-level operational visibility: protected properties, active incidents, response times and resolution status across the insured base.' },
      { q: 'What information is recorded during an incident?', a: 'Each incident can create a complete timeline: when water was detected, when the customer was notified, when the alert was acknowledged, which actions were taken, whether assistance was involved, and when the incident was resolved.' },
    ],
  },
  finalCta: {
    line1: 'Insurance should not only pay for damage.',
    line2: 'It should help prevent it.',
    descriptor: 'Connected risk prevention for modern insurers.',
    cta: 'Talk to us',
  },
  contact: {
    label: 'Contact',
    h2: "Let's talk about insurance prevention.",
    sub: "Interested in exploring how connected prevention could support your insurance portfolio? Let's discuss it.",
  },
};

export const e2lv = {
  meta: {
    title: 'Ūdens noplūžu atklāšana apdrošināšanai | E2 Insurance',
    description: 'E2 Insurance palīdz apdrošinātājiem atklāt ūdens noplūdes agrāk, nodrošināt ātrāku iejaukšanos un samazināt ūdens radīto zaudējumu apmēru, izmantojot savienotu risku novēršanu.',
    twitterDescription: 'Savienota risku novēršana moderniem apdrošinātājiem. Atklājiet agrāk, reaģējiet ātrāk, samaziniet zaudējumu apmēru.',
  },
  hero: {
    badge: 'E2 Insurance — Savienota risku novēršana',
    h1: 'Novērsiet ūdens radītos bojājumus, pirms tie kļūst par lielu atlīdzības prasību.',
    sub: 'E2 Insurance palīdz apdrošinātājiem laikus atklāt ūdens noplūdes, nekavējoties brīdināt īstos cilvēkus un samazināt ūdens radīto incidentu ietekmi.',
    supporting: 'No atlīdzību administrēšanas uz aktīvu risku novēršanu.',
    ctaPrimary: 'Iepazīstiet E2 Insurance',
    ctaSecondary: 'Sazinieties ar mums',
    trustLine: 'Radīts apdrošinātājiem, palīdzības dienestiem un moderniem mājokļa apdrošināšanas produktiem.',
    liveLabel: 'Aktīvs incidents',
    resolved: 'ATRISINĀTS',
    incidentTitle: 'Konstatēts ūdens — Virtuve',
    incidentLoc: 'Rīga · Dzīvoklis 4B',
    steps: [
      ['02:14', 'Konstatēts ūdens zem virtuves izlietnes'],
      ['02:15', 'Klients informēts'],
      ['02:17', 'Klients apstiprināja brīdinājumu'],
      ['02:46', 'Incidents atrisināts'],
    ],
    tta: 'Apstiprināšanas laiks',
    ttaVal: '3 min',
    ttr: 'Atrisināšanas laiks',
    ttrVal: '32 min',
  },
  problem: {
    label: 'Problēma',
    h2: 'Ūdens radītie bojājumi kļūst dārgi, kad neviens nezina, ka tie notiek.',
    p1: 'Neliela noplūde var palikt nepamanīta stundām vai dienām. Brīdī, kad apdrošinājuma ņēmējs to atklāj, bojājumi jau var skart grīdas, sienas, mēbeles, kaimiņu īpašumus un radīt ievērojamas atjaunošanas izmaksas.',
    p2: 'Tradicionālā apdrošināšana sākas, kad tiek pieteikta atlīdzības prasība.',
    highlight: 'E2 Insurance sākas brīdī, kad sākas incidents.',
    statement: 'Visdārgākā noplūde ir tā, par kuru neviens nezina.',
  },
  compare: {
    label: 'Atšķirība',
    h2: 'Laiks maina iznākumu.',
    withoutTitle: 'Bez agrīnas atklāšanas',
    withoutSteps: ['Sākas noplūde', 'Noplūde paliek nepamanīta', 'Ūdens bojājumi izplatās', 'Klients atklāj bojājumus', 'Tiek pieteikta atlīdzības prasība', 'Sākas atjaunošanas darbi'],
    withoutNote: 'Var paiet stundas vai dienas, pirms kāds reaģē.',
    withTitle: 'Ar E2 Insurance',
    withSteps: ['Sākas noplūde', 'Konstatēts ūdens', 'Klients brīdināts', 'Incidents apstiprināts', 'Veikta rīcība', 'Bojājumi, iespējams, ierobežoti'],
    withNote: 'Minūtes no atklāšanas līdz rīcībai.',
    conclusion: 'E2 pārvērš laiku par risku samazināšanas instrumentu.',
  },
  value: {
    label: 'Kā tas darbojas',
    h2: 'Atklājiet agrāk. Reaģējiet ātrāk. Samaziniet bojājumus.',
    stepLabel: 'Solis',
    steps: [
      { title: 'Atklāt', desc: 'Ūdens tiek identificēts, tiklīdz notiek incidents.' },
      { title: 'Brīdināt', desc: 'Īstie cilvēki tiek informēti automātiski.' },
      { title: 'Reaģēt', desc: 'Apdrošinājuma ņēmējs, palīdzības dienests vai apkopes komanda var ātri iejaukties.' },
      { title: 'Dokumentēt', desc: 'Katru incidentu un rīcību var reģistrēt operatīvai uzraudzībai un analīzei.' },
    ],
  },
  metrics: {
    label: 'Izmērāma novēršana',
    h2: 'Mēriet novēršanu ar reakcijas laiku.',
    items: [
      { label: 'Atklāšanas laiks', desc: 'Cik ātri incidents tiek identificēts.' },
      { label: 'Apstiprināšanas laiks', desc: 'Cik ātri kāds apstiprina brīdinājumu.' },
      { label: 'Atrisināšanas laiks', desc: 'Cik ātri situācija tiek pilnībā kontrolēta.' },
    ],
    statement: 'Novēršana kļūst izmērāma, kad katram incidentam ir laika grafiks.',
  },
  gains: {
    label: 'Vērtība apdrošināšanai',
    h2: 'Pārvērtiet novēršanu izmērāmā apdrošināšanas vērtībā.',
    items: [
      { title: 'Samaziniet zaudējumu apmēru', desc: 'Agrāka atklāšana var ierobežot bojājumu apjomu pirms iejaukšanās.' },
      { title: 'Samaziniet reakcijas laiku', desc: 'Brīdinājumi tiek ģenerēti automātiski, nevis paļaujoties, ka kāds problēmu galu galā pamanīs.' },
      { title: 'Uzlabojiet klientu pieredzi', desc: 'Apdrošinātājs palīdz klientiem novērst bojājumus, nevis tikai kompensē tos pēc tam.' },
      { title: 'Radiet izmantojamus risku datus', desc: 'Iegūstiet strukturētu informāciju par incidentiem, reakcijas laikiem un novēršanas sniegumu.' },
      { title: 'Stipriniet klientu noturību', desc: 'Risku novēršanas pakalpojumi var kļūt par taustāmu priekšrocību mājokļa apdrošināšanas produktos.' },
      { title: 'Izceliet savus produktus', desc: 'Piedāvājiet klientiem proaktīvu pakalpojumu ārpus tradicionālā seguma un cenas.' },
    ],
  },
  incident: {
    label: 'Incidentu izsekojamība',
    h2: 'No incidenta līdz atrisinājumam — redzama katra sekunde.',
    sub: 'Katrs incidents veido pilnīgu, ar laika zīmogiem fiksētu notikumu vēsturi — no pirmās atklāšanas līdz galīgajam atrisinājumam.',
    cardTitle: 'Konstatēts ūdens — Virtuve',
    cardId: 'Incidents #2481 · Piemēra notikums',
    status: 'Atrisināts',
    events: [
      ['02:14', 'Konstatēts ūdens zem virtuves izlietnes'],
      ['02:15', 'Klients informēts'],
      ['02:17', 'Klients apstiprināja brīdinājumu'],
      ['02:21', 'Sazināts ar palīdzības dienestu'],
      ['02:29', 'Uzsākta rīcība'],
      ['02:46', 'Incidents atrisināts'],
    ],
    tta: 'Apstiprināšanas laiks',
    ttaVal: '3 min',
    ttr: 'Atrisināšanas laiks',
    ttrVal: '32 min',
    note: 'Konceptuāls piemērs ilustrācijai — nav reāla darbības statistika.',
  },
  customer: {
    label: 'Klientu pieredze',
    h2: 'Aizsardzība bez sarežģītības.',
    p1: 'E2 Insurance ir veidots tā, lai darbotos automātiski fonā. Apdrošinājuma ņēmējam nav jāsaprot vai jāuzrauga tehniskā infrastruktūra.',
    p2: 'Kad kaut kas notiek, klients saņem vienkāršu, uz rīcību vērstu brīdinājumu.',
    e2homeSub: 'Vienkārša aizsardzība apdrošinājuma ņēmējam.',
    features: [
      'Īpašuma aizsardzības statuss',
      'Aktīvie brīdinājumi',
      'Konstatēto incidentu atrašanās vieta',
      'Push paziņojumi',
      'Incidenta apstiprināšana',
      'Vienkāršas instrukcijas',
      'Incidentu vēsture',
      'Saziņa ar palīdzības dienestu',
      'Apstiprinājums, kad incidents ir atrisināts',
    ],
    highlight: 'Lietotnei ir viens mērķis: palīdzēt klientam ātri rīkoties, kad kaut kas notiek.',
    mockup: {
      homeStatus: 'Mājas statuss',
      protected: 'Aizsargāts',
      alertTitle: 'Konstatēts ūdens',
      alertLoc: 'Virtuve · Zem izlietnes · 14:32',
      recommended: 'Ieteicamā rīcība',
      recommendedAction: 'Nekavējoties pārbaudiet vietu.',
      btnCheck: 'Es pārbaudu',
      btnAssist: 'Sazināties ar palīdzību',
    },
  },
  control: {
    label: 'Apdrošinātājam',
    h2: 'Viens skats uz visu apdrošināto portfeli.',
    sub: 'E2 Insurance Control Center sniedz operatīvajām komandām reāllaika pārskatu par katru aizsargāto īpašumu un katru aktīvo incidentu.',
    sampleTag: 'PIEMĒRA SASKARNE — ILUSTRATĪVAS VĒRTĪBAS',
    kpis: [
      { label: 'Aizsargātie īpašumi', value: '12 482' },
      { label: 'Aktīvie incidenti', value: '7' },
      { label: 'Vidējais apstiprināšanas laiks', value: '2 min 18 s' },
      { label: 'Šomēnes atrisinātie incidenti', value: '164' },
    ],
    tableHeaders: ['Īpašums', 'Incidenta veids', 'Vieta', 'Konstatēts', 'Apstiprināts', 'Palīdzība', 'Statuss'],
    incidents: [
      { property: 'Rīga – Dzīvoklis 4B', type: 'Konstatēts ūdens', location: 'Virtuve', detected: '08:42', ack: '08:45', assistance: 'Nav nepieciešama', status: 'Atrisināts' },
      { property: 'Viļņa – Māja 12', type: 'Konstatēts ūdens', location: 'Vannas istaba', detected: '09:15', ack: '09:17', assistance: 'Sazināts', status: 'Procesā' },
      { property: 'Tallina – Dzīvoklis 7A', type: 'Konstatēts ūdens', location: 'Saimniecības telpa', detected: '10:03', ack: '—', assistance: '—', status: 'Jauns' },
      { property: 'Kauņa – Rindu māja 3', type: 'Konstatēts ūdens', location: 'Zem izlietnes', detected: '10:28', ack: '10:31', assistance: 'Nosūtīta', status: 'Palīdzība piesaistīta' },
      { property: 'Rīga – Dzīvoklis 22C', type: 'Konstatēts ūdens', location: 'Katlu telpa', detected: '11:05', ack: '11:06', assistance: 'Nav nepieciešama', status: 'Apstiprināts' },
    ],
    detectedShort: 'Konstatēts',
    note: 'Visi attēlotie skaitļi ir konceptuāli saskarnes piemēri, nevis reāli darbības dati.',
  },
  portfolio: {
    label: 'Portfeļa analītika',
    h2: 'Pārvērtiet incidentus portfeļa analītikā.',
    sub: 'E2 Insurance var palīdzēt apdrošinātājiem izprast novēršanas sniegumu visā portfelī — ne tikai atrisināt atsevišķus incidentus.',
    indicators: [
      'Aizsargāto īpašumu skaits',
      'Konstatēto incidentu skaits',
      'Incidentu biežums',
      'Vidējais apstiprināšanas laiks',
      'Vidējais atrisināšanas laiks',
      'Atkārtoti incidenti',
      'Vietas ar atkārtotiem notikumiem',
      'Neatrisinātie brīdinājumi',
      'Aizsardzības pieejamība',
      'Novēršanas sniegums laika gaitā',
    ],
    highlight: 'Atsevišķi brīdinājumi atrisina incidentus. Portfeļa analītika palīdz apdrošinātājiem pilnveidot novēršanas stratēģiju.',
  },
  assist: {
    label: 'Pēc izvēles — intervences partneriem',
    h2: 'Savienojiet palīdzības dienestu tieši ar incidentu.',
    p1: 'var nodrošināt palīdzības operatoriem vai pakalpojumu sniedzējiem mērķtiecīgu skatu uz incidentiem, kuriem nepieciešama iejaukšanās.',
    note: 'Apdrošinātāji izvēlas savu darbības modeli — E2 nepieprasa apdrošinātājiem izmantot E2 Assist.',
    panelTitle: 'E2 Assist — iespējamās funkcijas',
    functions: [
      'Piešķirtie incidenti',
      'Informācija par īpašumu',
      'Incidenta atrašanās vieta',
      'Atklāšanas laiks',
      'Klienta statuss',
      'Nepieciešamās darbības',
      'Intervences apstiprināšana',
      'Incidenta slēgšana',
      'Piezīmes',
    ],
  },
  platform: {
    label: 'Platforma',
    h2: 'Viena novēršanas platforma. Trīs darbības pieredzes.',
    items: [
      { name: 'E2 Home', who: 'Apdrošinājuma ņēmējam' },
      { name: 'E2 Insurance Control Center', who: 'Apdrošinātājam' },
      { name: 'E2 Assist', who: 'Palīdzības un intervences partneriem' },
    ],
    statement: 'E2 savieno atklāšanu, klienta reakciju, apdrošināšanas operācijas un palīdzību vienotā, izmērāmā incidentu darbplūsmā.',
  },
  workflow: {
    label: 'Darbplūsma',
    h2: 'No atklāšanas līdz atrisinājumam.',
    steps: [
      'Konstatēts ūdens',
      'Klients informēts',
      'Klients apstiprina',
      'Ja nepieciešams, informēta palīdzība',
      'Veikta rīcība',
      'Incidents atrisināts',
      'Notikums reģistrēts',
    ],
    note: 'E2 Insurance var pielāgot paziņojumu un eskalācijas noteikumus katra apdrošinātāja darbības modelim.',
  },
  fits: {
    label: 'Elastīga darbība',
    h2: 'Jūsu apdrošināšanas darbplūsma. Jūsu noteikumi.',
    sub: 'E2 neuzspiež apdrošinātājiem konkrētu darbības modeli. Incidents var iedarbināt dažādas darbplūsmas.',
    models: [
      { title: 'Klients vispirms', desc: 'Nekavējoties brīdiniet apdrošinājuma ņēmēju un eskalējiet tikai tad, ja nav atbildes.' },
      { title: 'Palīdzība vispirms', desc: 'Informējiet palīdzības centru vienlaikus ar klientu.' },
      { title: 'Eskalācija pakalpojumu sniedzējam', desc: 'Nosūtiet izvēlētos incidentus apstiprinātam intervences partnerim.' },
      { title: 'Apdrošinātāja kontrolē', desc: 'Novirziet incidentus caur apdrošinātāja paša operatīvo vidi.' },
    ],
    highlight: 'E2 pielāgojas apdrošinātājam — nevis otrādi.',
  },
  opportunity: {
    label: 'Produkta iespēja',
    h2: 'Padariet novēršanu par apdrošināšanas produkta daļu.',
    modelLabel: 'Modelis',
    models: [
      { title: 'Iekļautā aizsardzība', desc: 'Piedāvājiet E2 Insurance kā daļu no izvēlētām mājokļa apdrošināšanas polisēm.' },
      { title: 'Premium aizsardzība', desc: 'Piedāvājiet to kā papildu novēršanas pakalpojumu.' },
      { title: 'Augsta riska īpašumi', desc: 'Izvietojiet aizsardzību tur, kur ūdens radīto bojājumu iespējamais apmērs ir īpaši liels.' },
      { title: 'Portfeļa novēršana', desc: 'Izmantojiet E2 Insurance izvēlētos klientu segmentos, lai samazinātu kopējo risku.' },
      { title: 'White-label pakalpojums', desc: 'Sniedziet pakalpojumu ar apdrošinātāja paša zīmolu.' },
    ],
  },
  calculator: {
    label: 'Scenāriju modelēšana',
    h2: 'Izpētiet novēršanas iespējamo ekonomiku.',
    sub: 'Ievadiet savus portfeļa pieņēmumus, lai modelētu ilustratīvu scenāriju. Nekādi E2 pieņēmumi nav iepriekš ielādēti kā fakti.',
    inputsTitle: 'Jūsu pieņēmumi',
    properties: 'Apdrošināto īpašumu skaits',
    frequency: 'Ūdens bojājumu prasību gada biežums (%)',
    avgCost: 'Vidējās izmaksas uz vienu ūdens bojājumu prasību (€)',
    reduction: 'Pieņemtais vidējā zaudējumu apmēra samazinājums (%)',
    reductionNote: 'Jūsu pašu pieņēmums — nevis E2 snieguma apgalvojums.',
    resultsTag: 'Ilustratīvs scenārijs, balstīts uz jūsu ievadi',
    claims: 'Aplēstās ūdens bojājumu prasības gadā',
    exposure: 'Aplēstā ūdens bojājumu gada ekspozīcija',
    avoided: 'Iespējamie novērstie zaudējumi pēc jūsu pieņēmumiem',
    disclaimer: 'Šis kalkulators paredzēts tikai scenāriju modelēšanai. Rezultāti balstās vienīgi uz lietotāja ievadītajiem pieņēmumiem un negarantē ietaupījumus vai E2 sniegumu.',
  },
  businessCase: {
    label: 'Biznesa pamatojums',
    h2: 'Vienādojums ir vienkāršs.',
    statement: 'Cik lielus bojājumus var novērst, atklājot incidentu agrāk?',
    support: 'Apdrošinātājam pat salīdzinoši neliels vidējā zaudējumu apmēra samazinājums var radīt būtisku vērtību, ja to piemēro lielam apdrošināto portfelim.',
    highlight: 'E2 Insurance ir radīts, lai padarītu novēršanu izmērāmu.',
  },
  trust: {
    label: 'Uzticamība un pārvaldība',
    h2: 'Veidots uzticamām apdrošināšanas operācijām.',
    items: [
      { title: 'Droša piekļuve', desc: 'Uz lomām balstīta piekļuve operatīvajai informācijai.' },
      { title: 'Incidentu izsekojamība', desc: 'Notikumus un operatīvās darbības var reģistrēt visā incidenta dzīves ciklā.' },
      { title: 'Datu aizsardzība', desc: 'Veidots, domājot par datu aizsardzību un kontrolētu piekļuvi.' },
      { title: 'Konfigurējama datu glabāšana', desc: 'Glabāšanas politikas var konfigurēt atbilstoši operatīvajām un regulatīvajām prasībām.' },
      { title: 'Operatīvā redzamība', desc: 'Apdrošinātāji var uzraudzīt pakalpojuma un aizsardzības pieejamību.' },
    ],
  },
  adapt: {
    label: 'Pielāgojamība',
    h2: 'Tehnoloģijai jāpielāgojas apdrošināšanai — nevis otrādi.',
    sub: 'E2 Insurance var darboties kā neatkarīgs novēršanas pakalpojums vai integrēties esošajās apdrošināšanas un palīdzības vidēs.',
    caps: [
      'Klientu paziņojumu noteikumi',
      'Palīdzības procesi',
      'Eskalācijas procedūras',
      'Atlīdzību darbplūsmas',
      'Apdrošinātāja zīmols',
      'Portfeļa segmentācija',
      'Operatīvie pārskati',
      'Integrācija ar esošajām sistēmām',
    ],
  },
  whiteLabel: {
    label: 'Ieviešanas modelis',
    h2: 'Jūsu zīmols. Jūsu attiecības ar klientu.',
    p: 'E2 Insurance var atbalstīt white-label ieviešanas modeli: apdrošinātājs var piedāvāt novēršanas pakalpojumu ar savu zīmolu, kamēr E2 nodrošina pamatā esošo novēršanas infrastruktūru.',
    panelTitle: 'Konfigurējamie elementi',
    elements: ['Zīmolvedība', 'Klientu komunikācija', 'Paziņojumu darbplūsmas', 'Palīdzības procesi', 'Operatīvās saskarnes'],
  },
  faq: {
    label: 'BUJ',
    h2: 'Ūdens noplūžu atklāšana apdrošināšanai — biežākie jautājumi.',
    items: [
      { q: 'Kas ir ūdens noplūžu atklāšana apdrošināšanai?', a: 'Ūdens noplūžu atklāšana apdrošināšanai izmanto savienotu detektēšanu, lai identificētu ūdens incidentus pēc iespējas agrāk. Tā vietā, lai gaidītu, kamēr apdrošinājuma ņēmējs atklāj bojājumus, sistēma brīdina klientus, apdrošinātājus vai palīdzības dienestus, tiklīdz ūdens ir konstatēts, lai kāds varētu reaģēt, pirms bojājumi kļūst nopietnāki.' },
      { q: 'Kā agrīna ūdens noplūžu atklāšana var palīdzēt apdrošinātājiem?', a: 'Agrāka atklāšana rada laiku iejaukties. Šis laiks var nozīmēt ātrāku reakciju, iespējami mazāku zaudējumu apmēru, labāku klientu pieredzi un strukturētus incidentu datus. Apdrošinātāji iegūst pārskatu par to, kā incidenti attīstās un cik ātri tie tiek atrisināti visā apdrošinātajā portfelī.' },
      { q: 'Kas saņem ūdens noplūdes brīdinājumu?', a: 'Paziņojumu un eskalācijas noteikumi ir konfigurējami atbilstoši apdrošinātāja darbības modelim. Brīdinājumi var nonākt vispirms pie apdrošinājuma ņēmēja, paralēli palīdzības centrā, pie apstiprināta intervences partnera vai caur apdrošinātāja paša operatīvo vidi.' },
      { q: 'Vai klientam ir jāuzrauga sistēma?', a: 'Nē. Pakalpojums ir veidots tā, lai darbotos automātiski fonā. Apdrošinājuma ņēmējam nav jāpārbauda paneļi vai jāuzrauga aprīkojums. Kad kaut kas notiek, klients saņem vienkāršu, uz rīcību vērstu brīdinājumu ar skaidriem ieteicamajiem soļiem.' },
      { q: 'Vai E2 Insurance var integrēt ar esošu apdrošināšanas lietotni?', a: 'E2 Insurance ir veidots, lai atbalstītu integrāciju ar esošajām apdrošināšanas un palīdzības vidēm. Apdrošinātāji var iekļaut novēršanas brīdinājumus un incidentu datus savās klientu un operatīvajās darbplūsmās.' },
      { q: 'Vai E2 Insurance var izmantot ar savu zīmolu (white-label)?', a: 'Jā. Platforma var atbalstīt apdrošinātāja zīmola ieviešanas modeļus, kuros apdrošinātājs piedāvā novēršanas pakalpojumu ar savu zīmolu, kamēr E2 nodrošina pamatā esošo novēršanas infrastruktūru.' },
      { q: 'Vai apdrošinātāji var uzraudzīt vairākus īpašumus?', a: 'Jā. E2 Insurance Control Center ir veidots, lai nodrošinātu portfeļa līmeņa operatīvo redzamību: aizsargātie īpašumi, aktīvie incidenti, reakcijas laiki un atrisināšanas statuss visā apdrošinātajā bāzē.' },
      { q: 'Kāda informācija tiek reģistrēta incidenta laikā?', a: 'Katrs incidents var izveidot pilnīgu laika grafiku: kad tika konstatēts ūdens, kad klients tika informēts, kad brīdinājums tika apstiprināts, kādas darbības tika veiktas, vai bija iesaistīta palīdzība un kad incidents tika atrisināts.' },
    ],
  },
  finalCta: {
    line1: 'Apdrošināšanai nevajadzētu tikai maksāt par bojājumiem.',
    line2: 'Tai vajadzētu palīdzēt tos novērst.',
    descriptor: 'Savienota risku novēršana moderniem apdrošinātājiem.',
    cta: 'Sazinieties ar mums',
  },
  contact: {
    label: 'Kontakti',
    h2: 'Parunāsim par novēršanu apdrošināšanā.',
    sub: 'Vēlaties izpētīt, kā savienota novēršana varētu atbalstīt jūsu apdrošināšanas portfeli? Apspriedīsim to.',
  },
};
